package org.example.project.enginekartei

import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.example.project.manager.LumaticaApiManager
import org.example.project.enginetabular.loadWithTabularFilters
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.utils.All

object KarteiApi {
    private const val EMPTY_FIELD = "~"

    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    suspend fun loadMandanten(firmenId: String = currentFirmenId()): List<All> =
        decodeList(
            LumaticaApiManager.call(
                "kartei/mandanten/${apiPart(currentUserId())}/${apiPart(firmenId)}"
            )
        )

    suspend fun searchMandanten(filters: Map<String, String>): List<All> =
        decodeList(loadWithTabularFilters("kartei/mandanten-tabular", filters))

    suspend fun saveMandant(state: All, firmenId: String = currentFirmenId()): All? {
        val saved = decodeAll(
            LumaticaApiManager.call(
                listOf(
                    "kartei",
                    "mandant-save",
                    currentUserId(),
                    state.id,
                    state.firmenId,
                    state.position,
                    state.firma,
                    state.strasse,
                    state.hausnummer,
                    state.plz,
                    state.ort,
                    state.land,
                    state.ansprechpartner,
                    state.email,
                    state.telefon,
                    state.fax,
                    firmenId
                ).joinToString("/") { apiPart(it) }
            )
        )
        if (saved != null) {
            val companyPart = firmenId.ifBlank { currentFirmenId() }.ifBlank { "company" }
            val idPart = saved.id.ifBlank { state.id }.ifBlank { "mandant" }
            GlobalManager.markJsonChanged("mandant.json", "register/$companyPart/mandanten/$idPart/mandant.json")
        }
        return saved
    }

    suspend fun deleteMandanten(ids: List<String>, firmenId: String = currentFirmenId()): Boolean {
        val cleanIds = ids.map { it.trim() }.filter { it.isNotBlank() }
        if (cleanIds.isEmpty()) return true
        val raw = runCatching {
            LumaticaApiManager.call(
                listOf(
                    "kartei",
                    "mandant-delete",
                    currentUserId(),
                    cleanIds.joinToString(","),
                    firmenId
                ).joinToString("/") { apiPart(it) }
            )
        }.getOrDefault("")
        val ok = raw.contains("\"ok\":true")
        if (ok) {
            val companyPart = firmenId.ifBlank { currentFirmenId() }.ifBlank { "company" }
            GlobalManager.markJsonChanged("mandant.json", "register/$companyPart/mandanten/mandant.json")
        }
        return ok
    }

    suspend fun loadStandorte(): List<All> =
        decodeList(LumaticaApiManager.call("kartei/standorte/${apiPart(currentUserId())}/${apiPart(currentFirmenId())}"))

    suspend fun loadStandort(id: String): All? =
        decodeAll(LumaticaApiManager.call("kartei/standort/${apiPart(currentUserId())}/${apiPart(id)}/${apiPart(currentFirmenId())}"))

    suspend fun saveStandort(state: All): All? =
        decodeAll(
            LumaticaApiManager.call(
                listOf(
                    "kartei",
                    "standort-save",
                    currentUserId(),
                    state.id,
                    state.firma,
                    state.geschaeftsfuehrer,
                    state.istnetzwerk.toString(),
                    state.istkunde.toString(),
                    state.firmenId,
                    state.standort,
                    state.strasse,
                    state.hausnummer,
                    state.plz,
                    state.ort,
                    state.land,
                    state.telefon,
                    state.email,
                    state.palettentauschAktiv.toString(),
                    state.palettentauschWeAktiv.toString(),
                    state.palettentauschWaAktiv.toString(),
                    state.halleVon,
                    state.halleBis,
                    state.regalVon,
                    state.regalBis,
                    state.fachVon,
                    state.fachBis,
                    state.ebeneVon,
                    state.ebeneBis,
                    state.toreVon,
                    state.toreBis,
                    state.parkplatzVon,
                    state.parkplatzBis,
                    json.encodeToString(state.abteilungen),
                    currentFirmenId()
                ).joinToString("/") { apiPart(it) }
            )
        )

    suspend fun loadStandortDeckblatt(): All? =
        decodeAll(LumaticaApiManager.call("kartei/standort-deckblatt/${apiPart(currentUserId())}/${apiPart(currentFirmenId())}"))

    suspend fun saveStandortDeckblatt(state: All): All? =
        decodeAll(
            LumaticaApiManager.call(
                listOf(
                    "kartei",
                    "standort-deckblatt-save",
                    currentUserId(),
                    state.id,
                    state.firma,
                    state.geschaeftsfuehrer,
                    state.webseite,
                    state.gegruendetAm,
                    currentFirmenId()
                ).joinToString("/") { apiPart(it) }
            )
        )

    suspend fun loadRegisterFirma(): All? =
        decodeAll(LumaticaApiManager.call("kartei/register-firma/${apiPart(currentUserId())}/${apiPart(currentFirmenId())}"))

    suspend fun loadUnternehmen(firmenId: String = currentFirmenId()): All? =
        decodeAll(LumaticaApiManager.call("kartei/unternehmen/${apiPart(firmenId)}"))

    suspend fun saveUnternehmen(state: All, firmenId: String = currentFirmenId()): All? =
        decodeAll(
            LumaticaApiManager.call(
                listOf(
                    "kartei",
                    "unternehmen-save",
                    firmenId,
                    state.firma,
                    state.geschaeftsfuehrer,
                    state.webseite,
                    state.gegruendetAm,
                    state.strasse,
                    state.hausnummer,
                    state.plz,
                    state.ort,
                    state.email,
                    state.telefon,
                    state.mobil,
                    state.onboarding,
                    state.lizenzMonatlich,
                    state.crSatz,
                    state.extraMeeting,
                    state.extraSchulungRemote,
                    state.supportPreis,
                    state.cloudKosten,
                    state.archivierung,
                    state.backupkosten,
                    state.setupkosten,
                    state.steuernummer,
                    state.finanzamt,
                    state.umsatzsteuerId,
                    state.kreditinstitut,
                    state.iban
                ).joinToString("/") { apiPart(it) }
            )
        )

    private fun currentUserId(): String = LoginService.getId().trim()

    private fun currentFirmenId(): String =
        LoginService.getUser()?.firmenId.orEmpty().trim()
            .ifBlank { GlobalManager.currentFirmenId.trim() }

    private fun decodeList(raw: String): List<All> =
        if (raw.isBlank() || raw == "null") emptyList()
        else runCatching { json.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())

    private fun decodeAll(raw: String): All? =
        if (raw.isBlank() || raw == "null") null
        else runCatching { json.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()

    private fun apiPart(value: String): String {
        val clean = value.ifBlank { EMPTY_FIELD }
        return js("encodeURIComponent")(clean) as String
    }
}
